import React, { useContext, useState } from "react";

import classNames from "classnames";
import { motion } from "framer-motion";
import isEmpty from "lodash.isempty";
import styled from "styled-components";

import ComponentContext from "../../../contexts/ComponentContext";
import InteractiveGallery from "./InteractiveGallery";

const commonContainerStyles = `
  padding-top: 1.5rem;
  padding-bottom: 1.5rem;
  background-color: ${(props) => props.backgroundColour};
`;

const AnimatedContainer = styled.div`
  position: relative;
  overflow: hidden;
  ${commonContainerStyles}
  ${(props) => props.dynamicStyles || ""}
`;

const PlainContainer = styled.div`
  ${commonContainerStyles}
  ${(props) => props.dynamicStyles || ""}
`;

const BackgroundAnimationWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 0;
  pointer-events: none; /* Allows mouse events to pass through to content */
`;

function AnimationProvider({
  children,
  backgroundAnimations,
  className,
  dynamicStyles,
  backgroundColour,
}) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <AnimatedContainer
      className={className}
      dynamicStyles={dynamicStyles}
      backgroundColour={backgroundColour}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <BackgroundAnimationWrapper>
        {backgroundAnimations.map((anim, index) => {
          const variants = {
            initial: anim.animationConfig.initial,
            hover: anim.animationConfig.hover,
          };

          return (
            <motion.div
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              style={anim.animationConfig.style}
              variants={variants}
              initial="initial"
              animate={isHovered ? "hover" : "initial"}
              transition={anim.animationConfig.transition}
            >
              <img
                src={anim?.image?.url}
                style={anim.animationConfig.imgConfig}
                alt={anim?.image?.alternativeText || ""}
              />
            </motion.div>
          );
        })}
      </BackgroundAnimationWrapper>
      <div style={{ position: "relative", zIndex: 1 }}>{children}</div>
    </AnimatedContainer>
  );
}

function PerksWithImages() {
  const {
    backgroundColour = "#fff",
    headertext = "Refer a friend",
    fullWidth = false,
    componentClassName = "",
    CSS = "",
    customLeftArrowContent,
    customRightArrowContent,
    imageHeight,
    backgroundAnimations,
    perksList,
  } = useContext(ComponentContext);

  const hasAnimations = !isEmpty(backgroundAnimations);

  const rowClassName = classNames(
    { row: !fullWidth },
    componentClassName,
    "perks-with-images"
  );

  const content = (
    <InteractiveGallery
      headertext={headertext}
      items={perksList}
      imageHeight={imageHeight}
      customLeftArrowContent={customLeftArrowContent}
      customRightArrowContent={customRightArrowContent}
    />
  );

  if (hasAnimations) {
    return (
      <AnimationProvider
        backgroundAnimations={backgroundAnimations}
        className={rowClassName}
        dynamicStyles={CSS}
        backgroundColour={backgroundColour}
      >
        {content}
      </AnimationProvider>
    );
  }

  return (
    <PlainContainer
      backgroundColour={backgroundColour}
      dynamicStyles={CSS}
      className={rowClassName}
    >
      {content}
    </PlainContainer>
  );
}

export default PerksWithImages;
